import { useEffect, useState, useCallback, useMemo } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format, parseISO } from 'date-fns'
import { Trash2 } from 'lucide-react'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { Skeleton } from '../components/ui/skeleton'
import { Button } from '../components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog'
import { ExerciseSummaryRow } from '../components/training-diary/ExerciseSummaryRow'
import { WorkoutResultForm } from '../components/training-diary/WorkoutResultForm'
import { workoutsService } from '../services/workouts'
import { workoutResultsService } from '../services/workout-results'
import type { Exercise } from '../types/exercise'
import type { WorkoutResult, WorkoutResultPayload } from '../types/workout'

type Mode = 'edit' | 'create'

export default function WorkoutResultDetailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { resultId, workoutId } = useParams<{ resultId?: string; workoutId?: string }>()

  const mode: Mode = workoutId ? 'create' : 'edit'

  const [result, setResult] = useState<WorkoutResult | null>(null)
  const [exercise, setExercise] = useState<Exercise | null>(null)
  const [referenceDate, setReferenceDate] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // When arriving from a workout context, the navigation may carry a workoutId
  // (either via the URL for create, or via location state for edit). We use it
  // to wire the "Exercise" breadcrumb segment back to that workout page.
  const fromWorkoutId = useMemo<string | undefined>(() => {
    if (workoutId) return workoutId
    const state = location.state as { fromWorkoutId?: string } | null
    return state?.fromWorkoutId
  }, [workoutId, location.state])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      if (mode === 'edit' && resultId) {
        const r = await workoutResultsService.show(resultId)
        setResult(r)
        setExercise(r.exercise)
        setReferenceDate(r.completed_on)
      } else if (mode === 'create' && workoutId) {
        const w = await workoutsService.show(workoutId)
        setExercise(w.exercise)
        setReferenceDate(w.scheduled_date)
      }
    } catch {
      setExercise(null)
    } finally {
      setLoading(false)
    }
  }, [mode, resultId, workoutId])

  useEffect(() => { load() }, [load])

  // Breadcrumb: Logo | DD/MM | Exercise | Result(muted, hidden on narrow)
  const dateShort = referenceDate ? format(parseISO(referenceDate), 'dd/MM') : ''
  useBreadcrumb(
    () => ({
      onBack: () => navigate(-1),
      segments: [
        { label: dateShort || '…', onClick: () => navigate('/training-diary') },
        {
          label: exercise?.title ?? '…',
          onClick: fromWorkoutId
            ? () => navigate(`/training-diary/workouts/${fromWorkoutId}`)
            : undefined,
          muted: !fromWorkoutId,
        },
        {
          label: t('trainingDiary.workouts.resultDetail.crumb', 'Result'),
          muted: true,
          hideOnNarrow: true,
        },
      ],
    }),
    [t, navigate, dateShort, exercise?.title, fromWorkoutId]
  )

  async function handleSave(payload: WorkoutResultPayload) {
    if (mode === 'edit') {
      if (!result) return
      await workoutResultsService.update(result.id, payload)
    } else {
      if (!exercise) return
      await workoutResultsService.create({ ...payload, exercise_id: exercise.id })
    }
    navigate(-1)
  }

  async function handleDelete() {
    if (!result) return
    setDeleting(true)
    try {
      await workoutResultsService.destroy(result.id)
      navigate(-1)
    } finally {
      setDeleting(false)
      setDeleteDialogOpen(false)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-4 max-w-2xl mx-auto p-4">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    )
  }

  if (!exercise) {
    return (
      <div className="flex flex-col gap-3 max-w-2xl mx-auto p-4 text-center">
        <p className="text-muted-foreground">{t('trainingDiary.workouts.resultDetail.notFound', 'Result not found')}</p>
        <Button onClick={() => navigate('/training-diary')}>{t('common.back', 'Back')}</Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5 max-w-2xl mx-auto p-4 pb-12">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold leading-tight">
          {mode === 'create'
            ? t('trainingDiary.workouts.resultDetail.titleCreate', 'New result')
            : t('trainingDiary.workouts.resultDetail.title', 'Workout result')}
        </h1>
        <div className="rounded-md border p-2 bg-muted/40">
          <ExerciseSummaryRow exercise={exercise} compact />
        </div>
      </header>

      <WorkoutResultForm
        templates={exercise.templates}
        initial={
          mode === 'edit' && result
            ? {
                completed_on: result.completed_on,
                effort: result.effort,
                completion_percent: result.completion_percent,
                note: result.note,
                details: result.details,
              }
            : undefined
        }
        onSave={handleSave}
        onCancel={() => navigate(-1)}
      />

      {mode === 'edit' && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="self-start text-destructive hover:text-destructive"
          onClick={() => setDeleteDialogOpen(true)}
        >
          <Trash2 className="size-3 mr-1" />
          {t('trainingDiary.workouts.resultDetail.deleteButton', 'Delete result')}
        </Button>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('trainingDiary.workouts.resultDetail.deleteConfirmTitle', 'Delete this result?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'trainingDiary.workouts.resultDetail.deleteConfirmDescription',
                'This action cannot be undone.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? t('common.deleting', 'Deleting…') : t('common.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
